<article class="bg-white rounded-3xl border border-gray-200 p-6 2xl:p-8 w-full">

  <div *ngIf="totalEng === 0"
    class="col-span-12 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-700 mb-7">
    ⚠️ No engagement data yet — metrics will populate once the post receives interactions.
  </div>

  <!-- LOADING STATE -->
  <div *ngIf="loading" class="py-10 flex items-center justify-center">
    <div class="flex items-center gap-3 text-sm text-gray-600">
      <span class="inline-block w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin"></span>
      <span>Loading analytics…</span>
    </div>
  </div>

  <!-- ERROR STATE -->
  <div *ngIf="!loading && errorMsg" class="py-10">
    <div class="max-w-xl mx-auto rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      {{ errorMsg }}
    </div>
  </div>

  <!-- CONTENT -->
  <div *ngIf="!loading && !errorMsg" class="grid grid-cols-1 lg:grid-cols-12 gap-4">

    <!-- PERFORMANCE OVERVIEW -->
    <section class="col-span-1 lg:col-span-12 rounded-2xl border border-gray-200 p-4">
      <div class="flex items-start justify-between gap-4 mb-3">
        <div>
          <div class="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
            Performance Overview
          </div>
          <p class="mt-1 text-xs text-gray-500">
            How this post performs compared with your Format Average and the All-Posts median.
          </p>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-4 items-stretch">
        <!-- LEFT -->
        <div class="col-span-12 md:col-span-6 h-full">
          <div class="rounded-xl border border-gray-100 p-4 h-full flex flex-col justify-center">
            <div *ngIf="!showPieEmpty; else noEngagement" class="w-full" style="height: 200px;">
              <canvas #metricsPie></canvas>
            </div>

            <ng-template #noEngagement>
              <div class="w-full flex items-center justify-center h-[180px] text-sm text-gray-400">
                No engagement yet
              </div>
            </ng-template>
          </div>
        </div>

        <!-- RIGHT -->
        <div class="col-span-12 md:col-span-6 h-full">
          <div class="rounded-xl border border-gray-100 p-4 h-full flex flex-col justify-center">
            <div class="flex items-start justify-between gap-3 mb-3">
              <span
                class="px-3 py-1 rounded-full text-xs font-semibold inline-flex"
                [ngClass]="statusPillClass">
                {{ statusLabel }}
              </span>

              <div class="flex-col items-center text-center">
                <!-- <span class="text-[11px] text-gray-400">Compare against</span> -->
                <div class="flex items-center gap-0.5 bg-gray-100 rounded-full p-1 h-7">
                  <button
                    *ngFor="let p of periodOptions"
                    type="button"
                    (click)="setPeriod(p)"
                    class="px-3 h-6 rounded-full text-xs font-semibold transition-all inline-flex items-center justify-center"
                    [ngClass]="selectedPeriod === p
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'">
                    {{ p }}
                  </button>
                </div>
                <!-- <span class="text-[10px] text-gray-400"
                  matTooltip="Changes the period used to calculate the Global Median and Format Avg benchmarks only"
                  matTooltipPosition="above">
                  Compare against ↓
                </span> -->
              </div>
            </div>

            <div class="mb-4">
              <div class="text-2xl font-semibold text-gray-900 leading-tight">
                {{ engagementRate | number:'1.1-2' }}%
              </div>
              <div class="text-xs text-gray-500">
                Engagement rate
              </div>
            </div>

            <!-- Global median -->
            <div class="mb-4">
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <div
                    class="text-[11px] text-gray-500"
                    matTooltip="Comparison with the median engagement rate across all formats"
                    matTooltipPosition="above"
                    matTooltipShowDelay="250">
                    vs All-Posts Median
                    <span class="text-gray-700 font-medium">
                      {{ globalMedian | number:'1.1-2' }}%
                    </span>
                  </div>
                </div>

                <span
                  class="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
                  [ngClass]="{
                    'bg-blue-50 text-blue-700 border-blue-200': globalBar === 50,
                    'bg-emerald-50 text-emerald-700 border-emerald-200': globalBar > 50,
                    'bg-rose-50 text-rose-700 border-rose-200': globalBar < 50
                  }">
                  {{ deltaGlobal }}
                </span>
              </div>

              <div class="h-2 bg-gray-100 rounded-full overflow-hidden mt-2">
                <div
                  class="h-full rounded-full transition-all duration-500"
                  [ngClass]="{
                    'bg-blue-400': globalBar === 50,
                    'bg-emerald-500': globalBar > 50,
                    'bg-rose-400': globalBar < 50
                  }"
                  [style.width.%]="globalBar"></div>
              </div>

              <div class="mt-1 flex justify-between text-[10px] text-gray-400">
                <span>Below benchmark</span>
                <span>At benchmark</span>
                <span>Above benchmark</span>
              </div>
            </div>

            <!-- Format average -->
            <div>
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <div
                    class="text-[11px] text-gray-500"
                    matTooltip="Comparison with the average engagement rate for this content format"
                    matTooltipPosition="above"
                    matTooltipShowDelay="250">
                    vs Format Average
                    <span class="text-gray-700 font-medium">
                      {{ formatAvg | number:'1.1-2' }}%
                    </span>
                  </div>
                </div>

                <span
                  class="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
                  [ngClass]="deltaFormat === '0pp'
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : deltaFormat.startsWith('+')
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-rose-50 text-rose-700 border-rose-200'">
                  {{ deltaFormat }}
                </span>
              </div>

              <div class="h-2 bg-gray-100 rounded-full overflow-hidden mt-2">
                <div
                  class="h-full rounded-full transition-all duration-500"
                  [ngClass]="{
                    'bg-blue-400': formatBar === 50,
                    'bg-emerald-500': formatBar > 50,
                    'bg-rose-400': formatBar < 50
                  }"
                  [style.width.%]="formatBar"></div>
              </div>

              <div class="mt-1 flex justify-between text-[10px] text-gray-400">
                <span>Below average</span>
                <span>At average</span>
                <span>Above average</span>
              </div>
            </div>
            <p class="mt-3 inline-flex justify-center rounded-md bg-sky-50/70 px-2 py-1 text-center text-[11px] text-sky-700">
              Benchmarks are calculated from posts in the selected time window.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- METRIC BREAKDOWN -->
    <section class="col-span-12 md:col-span-6 rounded-2xl border border-gray-200 bg-white p-4">
      <div class="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-3">
        Metric Breakdown
      </div>

      <div>
        <!-- Activity -->
        <div
          class="mb-4 flex items-center justify-between gap-4"
          matTooltip="Activity = Total Clicks / Total Reach (views)"
          matTooltipPosition="above"
          matTooltipShowDelay="250">
          <div class="min-w-0">
            <div class="text-[11px] text-gray-500">Activity <span class="text-gray-400">(clicks)</span></div>
            <div class="text-sm font-semibold text-gray-900">{{ activity | number:'1.0-2' }}%</div>
          </div>

          <div class="flex-1">
            <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                [ngClass]="{
                  'bg-emerald-500': activityLabel === 'High',
                  'bg-amber-400': activityLabel === 'Avg',
                  'bg-rose-400': activityLabel === 'Low',
                  'bg-gray-300': activityLabel === 'None'
                }"
                [style.width.%]="activityBar">
              </div>
            </div>
            <div class="mt-1 flex justify-end">
              <span class="text-[11px] font-semibold" [ngClass]="metricLevelClass(activityLabel)">
                {{ activityLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Virality -->
        <div
          class="mb-4 flex items-center justify-between gap-4"
          matTooltip="Virality = Total Shares / Total Reach (views)"
          matTooltipPosition="above"
          matTooltipShowDelay="250">
          <div class="min-w-0">
            <div class="text-[11px] text-gray-500">Virality <span class="text-gray-400">(shares)</span></div>
            <div class="text-sm font-semibold text-gray-900">{{ virality | number:'1.0-2' }}%</div>
          </div>

          <div class="flex-1">
            <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                [style.width.%]="viralityBar"
                [ngClass]="{
                  'bg-emerald-500': viralityLabel === 'High',
                  'bg-amber-400': viralityLabel === 'Avg',
                  'bg-rose-400': viralityLabel === 'Low',
                  'bg-gray-300': viralityLabel === 'None'
                }">
              </div>
            </div>
            <div class="mt-1 flex justify-end">
              <span class="text-[11px] font-semibold" [ngClass]="metricLevelClass(viralityLabel)">
                {{ viralityLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Comments per Like -->
        <div
          class="mb-4 flex items-center justify-between gap-4"
          matTooltip="Comments per Like = Total Comments / Total Likes"
          matTooltipPosition="above"
          matTooltipShowDelay="250">
          <div class="min-w-0">
            <div class="text-[11px] text-gray-500">Comments per Like</div>
            <div class="text-sm font-semibold text-gray-900">{{ interactions | number:'1.0-2' }}</div>
          </div>

          <div class="flex-1">
            <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                [style.width.%]="interactionsBar"
                [ngClass]="{
                  'bg-emerald-500': interactionsLabel === 'High',
                  'bg-amber-400': interactionsLabel === 'Avg',
                  'bg-rose-400': interactionsLabel === 'Low',
                  'bg-gray-300': interactionsLabel === 'None'
                }">
              </div>
            </div>
            <div class="mt-1 flex justify-end">
              <span class="text-[11px] font-semibold" [ngClass]="metricLevelClass(interactionsLabel)">
                {{ interactionsLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Save Rate -->
        <div
          class="mb-4 flex items-center justify-between gap-4"
          matTooltip="Save Rate = Total Saves / Total Reach (views)"
          matTooltipPosition="above"
          matTooltipShowDelay="250">
          <div class="min-w-0">
            <div class="text-[11px] text-gray-500">Save Rate <span class="text-gray-400">(saves)</span></div>
            <div class="text-sm font-semibold text-gray-900">{{ savesRate | number:'1.0-2' }}%</div>
          </div>

          <div class="flex-1">
            <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                [style.width.%]="savesRateBar"
                [ngClass]="{
                  'bg-emerald-500': savesRateLabel === 'High',
                  'bg-amber-400': savesRateLabel === 'Avg',
                  'bg-rose-400': savesRateLabel === 'Low',
                  'bg-gray-300': savesRateLabel === 'None'
                }">
              </div>
            </div>
            <div class="mt-1 flex justify-end">
              <span class="text-[11px] font-semibold" [ngClass]="metricLevelClass(savesRateLabel)">
                {{ savesRateLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Saves per Like -->
        <div
          class="flex items-center justify-between gap-4"
          matTooltip="Saves per Like = Total Saves / Total Likes"
          matTooltipPosition="above"
          matTooltipShowDelay="250">
          <div class="min-w-0">
            <div class="text-[11px] text-gray-500">Saves per Like</div>
            <div class="text-sm font-semibold text-gray-900">{{ savesToLikes | number:'1.0-2' }}</div>
          </div>

          <div class="flex-1">
            <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                [style.width.%]="savesToLikesBar"
                [ngClass]="{
                  'bg-emerald-500': saveToLikesLabel === 'High',
                  'bg-amber-400': saveToLikesLabel === 'Avg',
                  'bg-rose-400': saveToLikesLabel === 'Low',
                  'bg-gray-300': saveToLikesLabel === 'None'
                }">
              </div>
            </div>
            <div class="mt-1 flex justify-end">
              <span class="text-[11px] font-semibold" [ngClass]="metricLevelClass(saveToLikesLabel)">
                {{ saveToLikesLabel }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- AI INSIGHTS -->
    <section class="col-span-12 md:col-span-6 rounded-2xl border border-gray-200 p-5 flex flex-col">
      <div class="flex items-center justify-between mb-3">
        <div class="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
          AI Insights
        </div>

        <button
          *ngIf="aiOpen && !aiLoading"
          type="button"
          (click)="toggleAiInsights()"
          class="text-xs font-semibold text-gray-600 hover:text-gray-900">
          Close
        </button>
      </div>

      <div *ngIf="!aiOpen" class="flex-1 flex flex-col items-center justify-center">
        <button
          type="button"
          (click)="toggleAiInsights()"
          [disabled]="engagementRate === 0 && totalEng === 0"
          class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-white text-xs font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          <span>✨</span> Generate AI Insights
        </button>
        <p
          *ngIf="engagementRate === 0 && totalEng === 0"
          class="text-xs text-gray-400 mt-2 text-center">
          Insights available once post has engagement data
        </p>
      </div>

      <!-- Loading state -->
      <div *ngIf="aiOpen && aiLoading" class="flex-1 flex flex-col items-center justify-center gap-3">
        <span class="inline-block w-6 h-6 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin"></span>
        <span class="text-xs text-gray-400">Analyzing your post…</span>
      </div>

      <!-- Content state -->
      <div *ngIf="aiOpen && !aiLoading">

        <!-- Summary -->
        <div *ngIf="aiSummary" class="mb-4 rounded-xl bg-indigo-50 border border-indigo-100 p-3">
          <div class="text-[10px] uppercase tracking-wide text-indigo-400 font-semibold mb-1">Summary</div>
          <div class="text-sm text-indigo-900 leading-relaxed">{{ aiSummary }}</div>
        </div>

        <!-- Actions -->
        <div *ngIf="aiActions && aiActions.length > 0" class="mb-4">
          <div class="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Recommended Actions</div>
          <div>
            <div
              *ngFor="let a of aiActions; let i = index"
              class="mb-2 flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div class="shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold flex items-center justify-center">
                {{ i + 1 }}
              </div>
              <div class="text-sm text-gray-700 leading-snug">{{ a }}</div>
            </div>
          </div>
        </div>

        <!-- Empty -->
        <div
          *ngIf="!aiSummary && (!aiActions || aiActions.length === 0)"
          class="text-sm text-gray-400 text-center py-4">
          No insights available for this post.
        </div>
      </div>
    </section>
  </div>
</article>